/**
 * The tour's eight steps (W2-14 onboarding-wizard, content pass W3-08; DESIGN.md §5.14): the line in the preview ->
 * Play -> place a Tap -> place a Hold -> drag the line (the gizmo's keyframe rule) -> change the curve -> the
 * Problems chip -> Publish.
 *
 * Each step names the part of the editor it points at (a "Show me" target, with fallbacks), the part the card must
 * not cover (the rest of the tool rail for the Tap and Hold steps, whose target is a tool button), where
 * the card prefers to sit (`prefer`), what to prepare when it starts (the Chart workspace with the preview in Edit
 * mode first; motion columns shown for the motion steps) and how to notice that the user did it (playback started, a
 * Tap or Hold added, a keyframe written, a curve changed, Publish opened): a watched step completes by itself and the
 * tour moves on. The keys a step's text names come from the active keymap (`keys`: message parameter -> command),
 * never from the text.
 *
 * Steps 5 and 6 are one scripted mini-task (UX-091), so a new chart always gets through them:
 *  - "Make the line move" puts the playhead a bar in when it is at the start and turns Auto-key on while the step
 *    runs (restored afterwards), so any drag of the line writes a keyframe at the playhead and the line's motion has
 *    a segment; the step is done when a committed change leaves a position or rotation track with a keyframe after
 *    beat 0, and remembers that keyframe;
 *  - "Change how it moves" then selects the segment that ends at that keyframe (and focuses its column), so the curve
 *    key or a right-click opens the curve picker at once; its text says so.
 * Changes are watched on the store's COMMITS (`subscribeCommits`): a drag in the preview is a gesture whose commit
 * is never announced as a 'txn' change set, which is why the drag step used to wait forever.
 */

import type { AppServices } from '../../app/registry/types';
import type { KeyId, LineId, LineProp } from '../../core/model/types';
import type { Op, Txn } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { m as help } from './help.messages';
import { m } from './onboarding.messages';
import type { KeyRef } from './keys';
import { isCollapsedChip, type HelpTarget } from './showMe';

export type TourServices = Pick<
  AppServices,
  'store' | 'editor' | 'audio' | 'registry' | 'ui' | 'onChange' | 'i18n' | 'engine'
>;

/** A side of the target the card is placed on first when it fits there (CoachMark placement). */
export type TourCardSide = 'right' | 'left' | 'top' | 'bottom';

export interface TourStepDef {
  readonly id: 'line' | 'play' | 'tap' | 'hold' | 'drag' | 'curve' | 'problems' | 'publish';
  readonly title: MessageKey;
  readonly body: MessageKey;
  readonly target: HelpTarget;
  /**
   * The part the card keeps clear of (CoachMark `avoid`): where the user acts next, or the controls around a small
   * target (the rest of the tool rail around a tool button).
   */
  readonly avoid?: HelpTarget;
  /**
   * The side of the target the card goes to when it fits there (UX-095: the Tap step's card right of the Tap
   * button, not far away over another panel); otherwise the usual order. Not read by Tour.tsx / CoachMark yet
   * (W3-07, change request CR-W3-08-4): until then `avoid` gives the same placement.
   */
  readonly prefer?: TourCardSide;
  /** Text used instead of `body` when the step's part was found in another form (e.g. a chip in the More menu). */
  readonly altBody?: { readonly when: (el: Element) => boolean; readonly body: MessageKey };
  /** Keys the body names: message parameter -> the command whose current binding is shown. */
  readonly keys?: Readonly<Record<string, KeyRef>>;
  /** Runs when the step starts. */
  readonly prepare?: (s: TourServices) => void;
  /** Calls `done` once the user did what the step asks; returns the unsubscribe. */
  readonly watch?: (s: TourServices, done: () => void) => Unsubscribe;
}

const noop: Unsubscribe = () => undefined;

/**
 * What the Tap and Hold cards keep clear of: the tool rail, so the card goes right of the tool button (over the top
 * of the lane, which leaves the rest of the lane free to click in) instead of far away beside the lane (UX-095).
 */
const TOOL_RAIL: HelpTarget = { helpId: 'tool-rail' };

/**
 * Puts the `tool-<tool>` help id the Tap and Hold steps target onto the rail's button (UX-095: the ring must cover
 * the one button, not the whole rail).
 *
 * `ToolRail` marks its Full-level `IconButton` with `helpId`, but its guided `Button` branch passes
 * `data-help-id` as a plain attribute, which `ui/Button` overwrites with its own `helpId` prop after the rest
 * spread (undefined) — so at the Guided level the tour targets, the button carries no help id and the step falls
 * back to the whole rail. Stamping the id onto the `[data-tool]` button (which both branches render) gives the
 * tour its per-button target. It is a no-op wherever the id is already there (IconButton, or ToolRail passing
 * `helpId` itself once CR-W3-08-8 lands). Re-stamped while the step runs: a remount of the rail (a workspace
 * switch) drops the attribute again, and React does not rewrite an attribute whose prop never changed.
 */
export function stampToolHelpId(tool: string): void {
  const sel = `[data-tool="${tool.replace(/["\\]/g, '\\$&')}"]`;
  for (const el of document.querySelectorAll(sel)) {
    if (el.getAttribute('data-help-id') === null) el.setAttribute('data-help-id', `tool-${tool}`);
  }
}

/**
 * The Tap / Hold step's watch: keeps its per-button help id on screen (see `stampToolHelpId`) and completes when
 * a note of the right kind is committed.
 */
function watchTool(tool: 'tap' | 'hold', hold: boolean): (s: TourServices, done: () => void) => Unsubscribe {
  return (s, done) => {
    stampToolHelpId(tool);
    const timer = window.setInterval(() => {
      stampToolHelpId(tool);
    }, 250);
    const off = watchCommits(s, (txn) => addsNote(txn.ops, hold), done);
    return () => {
      window.clearInterval(timer);
      off();
    };
  };
}

/** Line properties the move step accepts (what the preview's gizmo drags and turns). */
const MOVE_PROPS: ReadonlySet<LineProp> = new Set<LineProp>(['moveX', 'moveY', 'rotate']);

/** Where the playhead goes for the move step when it is at the very start: one 4/4 bar in. */
export const TOUR_MOVE_BEAT = 4;

/**
 * Tour progress shared by steps 5 and 6: the keyframe the move step wrote (its line, column and key). Reset when
 * the tour starts again (the first step's `prepare`).
 */
interface TourMotion {
  readonly line: LineId;
  readonly prop: LineProp;
  readonly layer: number;
  readonly key: KeyId;
}

let tourMotion: TourMotion | null = null;

/** The keyframe the move step wrote, if it still exists in the open chart (tests and the curve step). */
export function tourMotionKey(s: Pick<TourServices, 'store'>): TourMotion | null {
  const m0 = tourMotion;
  if (m0 === null || s.store?.doc.keys.has(m0.key) !== true) return null;
  return m0;
}

/** Forgets the tour's progress (a new tour). */
export function resetTourProgress(): void {
  tourMotion = null;
}

/**
 * Watches committed transactions of the open chart (re-attaching when the chart changes): ordinary transactions and
 * the commits of gestures (drags), not undo and redo.
 */
function watchCommits(s: TourServices, test: (txn: Txn) => boolean, done: () => void): Unsubscribe {
  let off: Unsubscribe = noop;
  const attach = (): void => {
    off();
    const store = s.store;
    off =
      store === null
        ? noop
        : store.subscribeCommits((txn) => {
            if (txn.kind === 'do' && test(txn)) done();
          });
  };
  attach();
  const offChange = s.onChange(attach);
  return () => {
    off();
    offChange();
  };
}

/** A note of `kind` (or any non-hold kind) was added. */
function addsNote(ops: readonly Op[], hold: boolean): boolean {
  return ops.some((op) => op.o === 'ins' && op.tbl === 'notes' && (op.rec.kind === 'hold') === hold);
}

/**
 * The keyframe a committed change wrote on a line's position or rotation, when its track now has a segment that
 * ends there (a keyframe after beat 0 with another one before it).
 */
export function movedKey(s: Pick<TourServices, 'store'>, ops: readonly Op[]): TourMotion | null {
  const store = s.store;
  if (store === null) return null;
  const { doc, idx } = store;
  for (const op of ops) {
    let id: KeyId;
    if (op.o === 'ins' && op.tbl === 'keys') id = op.rec.id;
    else if (op.o === 'set' && op.tbl === 'keys') id = op.id;
    else continue;
    const key = doc.keys.get(id);
    if (key === undefined || !Beats.lt(Beats.ZERO, key.t)) continue;
    const owner = doc.tracks.get(key.track)?.owner;
    if (owner?.kind !== 'line' || !MOVE_PROPS.has(owner.prop)) continue;
    if (idx.prevKey(id) === undefined) continue;
    return { line: owner.line, prop: owner.prop, layer: owner.layer, key: id };
  }
  return null;
}

/** A segment's curve was changed. */
function changesCurve(ops: readonly Op[]): boolean {
  return ops.some((op) => op.o === 'set' && op.tbl === 'keys' && 'ease' in op.next);
}

function showMotionColumns(s: TourServices): void {
  const e = s.editor.getState();
  if (!e.timeline.showMotionColumns) e.setTimeline({ showMotionColumns: true });
}

/** The Chart workspace with the preview in Edit mode (and the gizmo on, for the move step). */
function chartInEditMode(s: TourServices, gizmo: boolean): void {
  if (s.store !== null && s.editor.getState().workspace !== 'chart') {
    void s.registry.run('app.workspace', { workspace: 'chart' }, 'api');
  }
  s.editor.getState().setPreview(gizmo ? { mode: 'edit', gizmo: true } : { mode: 'edit' });
}

/** Makes sure a line is active (the gizmo drags the active line) and the playhead is not at the very start. */
function prepareMove(s: TourServices): void {
  chartInEditMode(s, true);
  showMotionColumns(s);
  const st = s.editor.getState();
  const store = s.store;
  if (store !== null && (st.activeLine === null || !store.doc.lines.has(st.activeLine))) {
    const first = store.doc.lineOrder[0];
    if (first !== undefined) st.setActiveLine(first);
  }
  const tempo = s.engine?.tempo ?? null;
  if (tempo === null) return;
  // A keyframe at beat 0 would move the line for the whole chart: give the drag a moment later in the song.
  let here: number;
  try {
    here = tempo.beatF64At(s.audio.transport.chartTime());
  } catch {
    here = 0;
  }
  if (here < 1) s.audio.transport.seek(tempo.secAt(Beats.fromInt(TOUR_MOVE_BEAT)));
}

/** Auto-key on while the move step runs; turned off again afterwards unless the user changed it meanwhile. */
function autoKeyWhile(s: TourServices): Unsubscribe {
  const st = s.editor.getState();
  if (st.preview.autoKey) return noop;
  st.setPreview({ autoKey: true });
  return () => {
    const now = s.editor.getState();
    if (now.preview.autoKey) now.setPreview({ autoKey: false });
  };
}

/** Selects the segment that ends at the move step's keyframe and focuses its column. */
function selectMovedSegment(s: TourServices): void {
  const moved = tourMotionKey(s);
  const store = s.store;
  if (moved === null || store === null) return;
  const start = store.idx.prevKey(moved.key);
  if (start === undefined) return;
  const st = s.editor.getState();
  if (st.activeLine !== moved.line) st.setActiveLine(moved.line);
  st.setFocusedColumn({ line: moved.line, prop: moved.prop, layer: moved.layer });
  st.select({ segments: new Set([start]), primary: { kind: 'segment', id: start } });
}

export const TOUR_STEPS: readonly TourStepDef[] = [
  {
    id: 'line',
    title: m.tourLineTitle,
    body: m.tourLineBody,
    target: { helpId: 'preview-stage' },
    prepare(s) {
      resetTourProgress();
      chartInEditMode(s, false);
    },
  },
  {
    id: 'play',
    title: m.tourPlayTitle,
    body: m.tourPlayBody,
    target: { helpId: 'transport.play' },
    keys: { play: { command: 'playback.toggle' }, listen: { command: 'playback.holdReturn' } },
    watch(s, done) {
      if (s.audio.transport.state.playing) {
        done();
        return noop;
      }
      return s.audio.transport.onChange((st) => {
        if (st.playing) done();
      });
    },
  },
  {
    id: 'tap',
    title: m.tourTapTitle,
    body: m.tourTapBody,
    // The Tap button itself (the rail's buttons carry `tool-<tool>` help ids; `watchTool` keeps the id on the
    // guided button, CR-W3-08-8), else the whole rail; the card keeps clear of the rest of the rail, so it sits
    // right of the button over the top of the lane (UX-095: "the card right of the rail", not over the Lines table).
    target: { helpId: 'tool-tap', panel: 'tool-rail' },
    avoid: TOOL_RAIL,
    prefer: 'right',
    keys: { tool: { command: 'tools.tap' }, place: { command: 'notes.placeTap' } },
    watch: watchTool('tap', false),
  },
  {
    id: 'hold',
    title: m.tourHoldTitle,
    body: m.tourHoldBody,
    target: { helpId: 'tool-hold', panel: 'tool-rail' },
    avoid: TOOL_RAIL,
    prefer: 'right',
    keys: { tool: { command: 'tools.hold' } },
    watch: watchTool('hold', true),
  },
  {
    id: 'drag',
    title: m.tourDragTitle,
    body: help.tourMoveBody,
    target: { helpId: 'preview-stage' },
    prepare: prepareMove,
    watch(s, done) {
      const restore = autoKeyWhile(s);
      const off = watchCommits(
        s,
        (txn) => {
          const moved = movedKey(s, txn.ops);
          if (moved === null) return false;
          tourMotion = moved;
          return true;
        },
        done,
      );
      return () => {
        off();
        restore();
      };
    },
  },
  {
    id: 'curve',
    title: m.tourCurveTitle,
    body: m.tourCurveBody,
    // After the move step: its segment is selected, so the text points at it.
    altBody: { when: () => tourMotion !== null, body: help.tourCurveSelectedBody },
    target: { helpId: 'motion-columns' },
    keys: { curve: { command: 'segments.easing', prefer: ['KeyC'] } },
    prepare(s) {
      showMotionColumns(s);
      selectMovedSegment(s);
    },
    watch: (s, done) => watchCommits(s, (txn) => changesCurve(txn.ops), done),
  },
  {
    id: 'problems',
    title: m.tourProblemsTitle,
    body: m.tourProblemsBody,
    altBody: { when: isCollapsedChip, body: m.tourProblemsBodyMore },
    target: { helpId: 'problems.chip' },
  },
  {
    id: 'publish',
    title: m.tourPublishTitle,
    body: m.tourPublishBody,
    target: { helpId: 'workspace.publish' },
    keys: { publish: { command: 'app.workspace', args: { workspace: 'publish' } } },
    watch(s, done) {
      if (s.editor.getState().workspace === 'publish') {
        done();
        return noop;
      }
      return s.editor.subscribe((st) => {
        if (st.workspace === 'publish') done();
      });
    },
  },
];
